/**
 * New dashboard or report: choose a starting point (blank, template, copy
 * of an existing one), see the template's layout before committing, name it.
 */

import { useState, type ReactNode } from 'react';

import { COLUMNS } from './grid.ts';
import type { Client, DocumentKind, Page, ReportDocument, Template } from './reports-api.ts';

type Start = 'blank' | 'template' | 'clone';

function LayoutPreview({ page }: { page: Page | undefined }): ReactNode {
  if (!page) return null;
  const rows = Math.max(1, ...page.widgets.map((widget) => widget.y + widget.h));
  return (
    <div
      className="rc-preview"
      style={{ aspectRatio: `${COLUMNS} / ${Math.max(rows, 6)}` }}
      aria-label="Layout preview"
    >
      {page.widgets.map((widget, index) => (
        <span
          key={index}
          className={`rc-box rc-box-${widget.type.toLowerCase()}`}
          style={{
            left: `${(widget.x / COLUMNS) * 100}%`,
            top: `${(widget.y / Math.max(rows, 6)) * 100}%`,
            width: `${(widget.w / COLUMNS) * 100}%`,
            height: `${(widget.h / Math.max(rows, 6)) * 100}%`,
          }}
          title={widget.display.title ?? widget.type}
        />
      ))}
    </div>
  );
}

export function CreateDialog({
  client,
  reports,
  templates: allTemplates,
  documents,
  onCancel,
  onCreate,
}: {
  client: Client;
  /** Creating a report (slide deck or document) rather than a dashboard. */
  reports: boolean;
  templates: Template[];
  documents: ReportDocument[];
  onCancel: () => void;
  onCreate: (input: {
    kind: DocumentKind;
    name: string;
    template?: string;
    clone_from?: string;
  }) => Promise<void>;
}): ReactNode {
  const [kind, setKind] = useState<DocumentKind>(reports ? 'SLIDE_DECK' : 'DASHBOARD');
  const templates = allTemplates.filter((item) => item.kinds.includes(kind));
  const sameKind = documents.filter((doc) => doc.kind === kind);
  const noun = reports ? 'report' : 'dashboard';
  const [start, setStart] = useState<Start>('template');
  const [template, setTemplate] = useState(templates[0]?.key ?? '');
  const [cloneFrom, setCloneFrom] = useState(sameKind[0]?.id ?? '');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const chosen = templates.find((item) => item.key === template);
  const suggested =
    start === 'template'
      ? (chosen?.name ?? `New ${noun}`)
      : start === 'clone'
        ? `${documents.find((doc) => doc.id === cloneFrom)?.name ?? 'Dashboard'} (copy)`
        : `${client.name} ${noun}`;

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await onCreate({
        kind,
        name: (name.trim() || suggested).slice(0, 160),
        ...(start === 'template' ? { template } : {}),
        ...(start === 'clone' ? { clone_from: cloneFrom } : {}),
      });
    } catch (error) {
      setProblem(error instanceof Error ? error.message : `The ${noun} could not be created.`);
      setBusy(false);
    }
  };

  return (
    <div
      className="rc-scrim"
      role="presentation"
      onClick={(event) => event.target === event.currentTarget && onCancel()}
    >
      <div
        className="rc"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rc-title"
        onKeyDown={(event) => event.key === 'Escape' && onCancel()}
      >
        <h2 id="rc-title">
          New {reports ? 'report' : 'dashboard'} for {client.name}
        </h2>
        {reports && (
          <div className="rb-seg rc-kind" role="radiogroup" aria-label="Report type">
            {(['SLIDE_DECK', 'DOCUMENT'] as const).map((value) => (
              <button
                type="button"
                role="radio"
                key={value}
                aria-checked={kind === value}
                className={kind === value ? 'on' : ''}
                onClick={() => {
                  setKind(value);
                  setCloneFrom(documents.find((doc) => doc.kind === value)?.id ?? '');
                }}
              >
                {value === 'SLIDE_DECK' ? 'Slide deck · for presenting' : 'Document · for reading'}
              </button>
            ))}
          </div>
        )}
        <div className="rc-starts" role="radiogroup" aria-label="Starting point">
          {(
            [
              ['blank', 'Blank', 'Start with an empty page'],
              ['template', 'Template', 'A ready layout you can change'],
              ['clone', 'Copy', `Duplicate one of this client’s ${noun}s`],
            ] as const
          ).map(([key, label, hint]) => (
            <button
              type="button"
              role="radio"
              aria-checked={start === key}
              key={key}
              disabled={key === 'clone' && sameKind.length === 0}
              className={`rc-start${start === key ? ' rc-start-on' : ''}`}
              onClick={() => setStart(key)}
            >
              <strong>{label}</strong>
              <small>{hint}</small>
            </button>
          ))}
        </div>

        {start === 'template' && (
          <div className="rc-templates">
            <ul role="listbox" aria-label="Templates">
              {templates.map((item) => (
                <li key={item.key}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={template === item.key}
                    className={`rc-template${template === item.key ? ' on' : ''}`}
                    onClick={() => setTemplate(item.key)}
                  >
                    <strong>{item.name}</strong>
                    <small>{item.description}</small>
                  </button>
                </li>
              ))}
            </ul>
            <LayoutPreview
              page={chosen?.pages.find((item) => (item.settings.layout ?? 'grid') === 'grid')}
            />
          </div>
        )}
        {start === 'clone' && (
          <label className="rs-field">
            Copy of
            <select value={cloneFrom} onChange={(event) => setCloneFrom(event.target.value)}>
              {sameKind.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {doc.name}
                </option>
              ))}
            </select>
          </label>
        )}

        <label className="rs-field">
          Name
          <input
            value={name}
            placeholder={suggested}
            maxLength={160}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        {problem && (
          <p className="rb-banner" role="alert">
            {problem}
          </p>
        )}
        <div className="rc-actions">
          <button type="button" className="btn secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || (start === 'clone' && !cloneFrom)}
            onClick={submit}
          >
            {busy ? 'Creating…' : 'Create and open'}
          </button>
        </div>
      </div>
    </div>
  );
}
